import type { FC } from 'react'
import { useRef, useState } from 'react'
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'
import type { LuciMessage, LuciRating } from './LuciService'
import { useRateAnswer } from './LuciService'

interface AnswerFeedbackProps {
  message: LuciMessage
  personId: number
}

/** Helpful or not, with a correction behind "not helpful"; every click sends the whole verdict. */
export const AnswerFeedback: FC<AnswerFeedbackProps> = ({ message, personId }) => {
  const rate = useRateAnswer(personId, message.conversation)
  const rating = message.feedback?.rating ?? null
  const correction = message.feedback?.correction ?? ''
  // The correction box is open while a draft is held, closed when it is null.
  const [draft, setDraft] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  useFocusOnOpen(inputRef, draft != null)

  const send = (next: LuciRating, text: string | null, onSuccess?: () => void) =>
    rate.mutate({ messageId: message.id, rating: next, correction: text }, { onSuccess })

  const markHelpful = () => {
    setDraft(null)
    if (rating !== 'helpful') send('helpful', null)
  }
  // The rating is recorded on the click, so an abandoned correction box still counts.
  const markNotHelpful = () => {
    setDraft(correction)
    if (rating !== 'not_helpful') send('not_helpful', null)
  }
  // The box closes once the save lands, so a rejected one keeps the text for another try.
  const saveCorrection = () => {
    if (rate.isPending) return
    send('not_helpful', draft?.trim() || null, () => setDraft(null))
  }

  return (
    <Stack sx={{ mt: 0.5 }} data-testid="luci-feedback">
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <IconButton
          size="small"
          aria-label="Helpful"
          aria-pressed={rating === 'helpful'}
          color={rating === 'helpful' ? 'primary' : 'default'}
          disabled={rate.isPending}
          onClick={markHelpful}
        >
          {rating === 'helpful' ? (
            <ThumbUpIcon fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label="Not helpful"
          aria-pressed={rating === 'not_helpful'}
          color={rating === 'not_helpful' ? 'primary' : 'default'}
          disabled={rate.isPending}
          onClick={markNotHelpful}
        >
          {rating === 'not_helpful' ? (
            <ThumbDownIcon fontSize="small" />
          ) : (
            <ThumbDownOutlinedIcon fontSize="small" />
          )}
        </IconButton>
        {rating === 'not_helpful' && draft == null && correction !== '' && (
          <Typography sx={{ color: 'text.secondary', fontSize: '1.3rem' }}>{correction}</Typography>
        )}
      </Stack>
      {draft != null && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 0.5, alignItems: 'flex-end' }}
          data-testid="luci-correction"
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            label="What should the answer have said?"
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key !== 'Enter' || event.shiftKey) return
              event.preventDefault()
              saveCorrection()
            }}
          />
          <Button
            size="small"
            variant="contained"
            disabled={rate.isPending}
            onClick={saveCorrection}
          >
            Save
          </Button>
          <Button size="small" onClick={() => setDraft(null)}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
